import type {
  BuildingUnitInfo,
  GroupSummary,
  FieldDefinition,
  InspectionDecision,
  InspectionItemData,
  InspectionSubitemData,
  ManagedUser,
  WorkflowStatus,
  ProjectOverview,
  ProjectBasicInfo,
  PendingSyncChange,
  ReceivingData,
  ExportStatus,
} from "./types";
import { INSPECTION_ITEM_DEFINITIONS } from "./inspection-data";
import { getReportTemplateDefaults } from "./report-defaults";

export const STORAGE_KEY = "report-assistant-h5-state";
export const ADMIN_ACCOUNT = "zihao1128";
export const ADMIN_PASSWORD = "45857803";
export const DEFAULT_USER_PASSWORD = "123456";
export const DEFAULT_GROUP_NAME = "默认分组";
export const DEFAULT_GROUP_ID = "group_default";
export const MAX_GROUP_PROJECTS = 15;

export const EMPTY_BASIC_INFO: ProjectBasicInfo = {
  projectCategory: "",
  projectName: "",
  projectAddress: "",
  constructionNature: [],
  constructionUnits: [""],
  designCompanies: [""],
  constructionCompanies: [""],
  supervisionCompanies: [""],
};

export const PROJECT_CATEGORY_OPTIONS = [
  "特殊建设工程",
  "其他建设工程",
] as const;

export const CONSTRUCTION_NATURE_OPTIONS = [
  "新建",
  "扩建",
  "迁建",
  "改建",
  "恢复",
  "拆除",
  "装修装饰",
  "其他",
] as const;

export const FIRE_FACILITY_OPTIONS = [
  "消火栓系统",
  "自动喷水灭火系统",
  "火灾自动报警系统",
  "防排烟系统",
  "泡沫灭火系统",
  "气体灭火系统",
  "建筑灭火器",
  "消防应急照明和疏散指示标志",
] as const;

export function createEmptyBuildingUnit(): BuildingUnitInfo {
  return {
    id: `building_${Math.random().toString(36).slice(2, 10)}`,
    buildingName: "",
    structureType: "",
    usageType: "",
    fireResistanceLevel: "",
    aboveGroundFloorCount: "",
    undergroundFloorCount: "",
    buildingHeight: "",
    aboveGroundBuildingArea: "",
    undergroundBuildingArea: "",
  };
}

export const EMPTY_OVERVIEW: ProjectOverview = {
  buildingUnits: [createEmptyBuildingUnit()],
  decorationPart: "",
  decorationArea: "",
  decorationFloors: "",
  insulationMaterialCategory: "",
  insulationFloors: "",
  insulationPart: "",
  insulationMaterial: "",
  fireFacilitySystems: [],
};

export const EMPTY_RECEIVING: ReceivingData = {
  acceptanceCode: "",
  acceptanceSummary: "",
  documentBody:
    "本项目消防验收/备案表信息完整；工程竣工验收报告的编制符合相关规定，竣工验收消防查验内容完整、符合要求；具有涉及消防的建设工程竣工图纸。消防审验主管部门于2025年xx月xx日对本项目出具受理凭证。",
  photos: [],
};

export const EMPTY_DECLARATION = {
  entryMode: "none",
  pdfFileName: "",
  pdfFileDataUrl: "",
  photoNames: [] as string[],
  photoDataUrls: [] as string[],
  skipped: false,
  parseStatus: "idle",
  sourceSummary: "",
  recognition: {
    status: "idle",
    summary: "尚未开始识别。",
    rawText: "",
    updatedAt: "",
    extractedBasicInfo: {},
    extractedOverview: {},
  },
} as const;

export const EMPTY_PENDING_SYNC_CHANGES: PendingSyncChange[] = [];

export const EMPTY_USERS: ManagedUser[] = [];

export const EMPTY_GROUPS: GroupSummary[] = [];

export const EMPTY_EXPORT_STATUS: ExportStatus = {
  reportPreviewGeneratedAt: "",
  recordPreviewGeneratedAt: "",
  reportPreviewRef: "",
  recordPreviewRef: "",
  reportPreviewFileName: "",
  recordPreviewFileName: "",
  reportGeneratedAt: "",
  recordGeneratedAt: "",
  reportExportRef: "",
  recordExportRef: "",
  reportFileName: "",
  recordFileName: "",
};

export const BASIC_INFO_FIELDS: FieldDefinition[] = [
  { key: "projectName", label: "项目名称", placeholder: "填写项目名称" },
  { key: "projectAddress", label: "工程地址", placeholder: "填写工程地址", multiline: true },
];

export const INSPECTION_DECISION_OPTIONS: {
  value: InspectionDecision;
  label: string;
}[] = [
  { value: "pending", label: "待填写" },
  { value: "pass", label: "合格" },
  { value: "fail", label: "不合格" },
  { value: "not_involved", label: "不涉及" },
];

export function getInspectionDecisionLabel(value: InspectionDecision): string {
  return (
    INSPECTION_DECISION_OPTIONS.find((option) => option.value === value)?.label ||
    "待填写"
  );
}

export const WORKFLOW_STATUS_OPTIONS: Array<{
  value: Exclude<WorkflowStatus, "not_involved">;
  label: string;
}> = [
  { value: "pending", label: "未完成" },
  { value: "in_progress", label: "进行中" },
  { value: "completed", label: "已完成" },
];

export function getWorkflowStatusLabel(value: WorkflowStatus): string {
  switch (value) {
    case "in_progress":
      return "进行中";
    case "completed":
      return "已完成";
    case "not_involved":
      return "不涉及";
    default:
      return "未完成";
  }
}

export function isWorkflowFinished(value: WorkflowStatus): boolean {
  return value === "completed" || value === "not_involved";
}

export function createEmptyInspectionItems(): InspectionItemData[] {
  return INSPECTION_ITEM_DEFINITIONS.map((item) => ({
    itemId: item.id,
    itemName: item.name,
    decision: "pending",
    workflowStatus: "pending",
    subitems: item.subitems.map<InspectionSubitemData>((subitem) => {
      const defaults = getReportTemplateDefaults(subitem.id);
      return {
        itemId: item.id,
        itemName: item.name,
        subitemId: subitem.id,
        subitemName: subitem.name,
        sampleCount: defaults?.sampleCount || "",
        sampledLocations: defaults?.sampledLocations || "",
        findings: defaults?.findings || "",
        decision: "pending",
        workflowStatus: "pending",
        photos: [],
      };
    }),
  }));
}
